import React, { useState } from 'react';
import { Box, Fab, Tooltip, Zoom } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { useRecomendaciones } from '@/context/RecomendacionesProvider';

const RecomendacionesFloatingMenu = ({ onRecomendacionSelect, ...props }) => {
    const { recomendaciones } = useRecomendaciones();
    const [isOpen, setIsOpen] = useState(false);

    const size = 25;
    const fontSize = '18px';

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleRecomendacionClick = (recomendacion) => {
        setIsOpen(false);
        onRecomendacionSelect(recomendacion);
    };

    // Calcular posición en arco para cada recomendación
    const getArcPosition = (index, total) => {
        let baseDistance = 45; // Distancia base desde el botón

        if (total > 3) {
            baseDistance += (total - 3) * 10;
        }

        // Calcular ángulo para distribuir en un arco de 180° (de arriba a abajo)
        const angleStep = 180 / (total - 1); // 180 grados de arco
        const angle = 90 - angleStep * index; // De 90° (arriba) a -90° (abajo)
        const rad = (angle * Math.PI) / 180;

        const x = Math.cos(rad) * baseDistance;
        const y = Math.sin(rad) * baseDistance;

        return {
            left: -x,
            top: -y,
        };
    };

    if (recomendaciones.length === 0) {
        return null;
    }

    // Si hay una sola recomendación, mostrar directamente el avatar
    if (recomendaciones.length === 1) {
        const recomendacion = recomendaciones[0];
        return (
            <Box
                sx={{
                    position: 'absolute',
                    zIndex: 1000,
                    ...props.sx,
                }}
                {...props}
            >
                <Tooltip
                    title={`${recomendacion.author?.nickname || 'Autor'} - ${recomendacion.title}`}
                    placement="left"
                >
                    <Fab
                        size="small"
                        onClick={() => handleRecomendacionClick(recomendacion)}
                        sx={{
                            backgroundColor: 'background.paper',
                            width: size,
                            height: size,
                            minHeight: size,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                transform: 'scale(1.15)',
                            },
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            border: '2px solid',
                            borderColor: 'primary.main',
                        }}
                    >
                        <CustomImageComponent
                            src={recomendacion.author?.image}
                            alt={recomendacion.author?.nickname || 'Autor'}
                            width={36}
                            height={36}
                            style={{
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    </Fab>
                </Tooltip>
            </Box>
        );
    }

    // Si hay múltiples recomendaciones, mostrar la lógica actual con estrella
    return (
        <Box
            sx={{
                position: 'absolute',
                zIndex: 1000,
                ...props.sx,
            }}
            {...props}
        >
            {/* Avatares flotantes en arco */}
            {recomendaciones.map((recomendacion, index) => {
                const arcPosition = getArcPosition(index, recomendaciones.length);
                return (
                    <Zoom
                        key={recomendacion.id}
                        in={isOpen}
                        style={{
                            transitionDelay: isOpen ? `${index * 50}ms` : '0ms',
                        }}
                        unmountOnExit
                    >
                        <Tooltip
                            title={`${recomendacion.author?.nickname || 'Autor'} - ${recomendacion.title}`}
                            placement="left"
                        >
                            <Fab
                                size="small"
                                onClick={() => handleRecomendacionClick(recomendacion)}
                                sx={{
                                    position: 'absolute',
                                    ...arcPosition,
                                    backgroundColor: 'background.paper',
                                    width: size,
                                    height: size,
                                    minHeight: size,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                        transform: 'scale(1.15)',
                                    },
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                }}
                            >
                                <CustomImageComponent
                                    src={recomendacion.author?.image}
                                    alt={recomendacion.author?.nickname || 'Autor'}
                                    width={36}
                                    height={36}
                                    style={{
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Fab>
                        </Tooltip>
                    </Zoom>
                );
            })}

            {/* Botón principal */}
            <Fab
                size="small"
                onClick={handleToggle}
                sx={{
                    position: 'relative',
                    backgroundColor: isOpen ? '#D81B60' : 'transparent',
                    width: size,
                    height: size,
                    minHeight: size,
                    '&:hover': {
                        backgroundColor: isOpen ? '#AD1457' : '#a126da',
                    },
                    transition: 'all 0.3s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    '& .MuiSvgIcon-root': {
                        fontSize: 16,
                        transition: 'all 0.3s ease',
                    },
                }}
            >
                {isOpen ? <CloseIcon /> : <span style={{ fontSize: fontSize }}>⭐</span>}
            </Fab>
        </Box>
    );
};

export default RecomendacionesFloatingMenu;
